import React, { useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { PlanProvider } from './context/PlanContext';
import { FarmModuleProvider, useFarmModules } from './context/FarmModuleContext';
import Layout from './components/layout/Layout';
import { PageSkeleton } from './components/shared/LoadingSpinner';

// Eagerly loaded (critical path)
import Login          from './pages/Login';

// Lazy loaded (deferrable routes)
const Forbidden = lazy(() => import('./pages/Forbidden'));
const NotFound = lazy(() => import('./pages/NotFound'));
const RegistrationSuccess = lazy(() => import('./pages/RegistrationSuccess'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Register = lazy(() => import('./pages/Register'));
// Dashboard pulls in recharts (~350KB); keeping it out of the entry bundle speeds up login/first paint.
const Dashboard       = lazy(() => import('./pages/Dashboard'));
const EggEntry        = lazy(() => import('./pages/EggEntry'));
const Rooms           = lazy(() => import('./pages/Rooms'));
const RoomDetail      = lazy(() => import('./pages/RoomDetail'));
const Incubator       = lazy(() => import('./pages/Incubator'));
const Expenses        = lazy(() => import('./pages/Expenses'));
const ExpenseComparison = lazy(() => import('./pages/ExpenseComparison'));
const ExpenseHistory  = lazy(() => import('./pages/ExpenseHistory'));
const Sales           = lazy(() => import('./pages/Sales'));
const SaleHistory     = lazy(() => import('./pages/SaleHistory'));
const Notifications   = lazy(() => import('./pages/Notifications'));
const Profile         = lazy(() => import('./pages/Profile'));
const Reports         = lazy(() => import('./pages/Reports'));
const Upgrade         = lazy(() => import('./pages/Upgrade'));
const Employees       = lazy(() => import('./pages/Employees'));
const EmployeeDetail  = lazy(() => import('./pages/EmployeeDetail'));
const Attendance      = lazy(() => import('./pages/Attendance'));
const Payroll         = lazy(() => import('./pages/Payroll'));
const Insights        = lazy(() => import('./pages/Insights'));
const Inventory       = lazy(() => import('./pages/Inventory'));
const InventoryDetail = lazy(() => import('./pages/InventoryDetail'));
const DairySheds        = lazy(() => import('./pages/DairySheds'));
const DairyShedProfile  = lazy(() => import('./pages/DairyShedProfile'));
const DairyAnimals      = lazy(() => import('./pages/DairyAnimals'));
const DairyAnimalProfile = lazy(() => import('./pages/DairyAnimalProfile'));
const DairyMilk         = lazy(() => import('./pages/DairyMilk'));
const DairyTrading        = lazy(() => import('./pages/DairyTrading'));
const DairyTradingPartyProfile = lazy(() => import('./pages/DairyTradingPartyProfile'));
const DairyHealth       = lazy(() => import('./pages/DairyHealth'));
const DairyBreeding     = lazy(() => import('./pages/DairyBreeding'));
const PWAInstallPrompt = lazy(() => import('./components/PWAInstallPrompt'));
const PWAUpdatePrompt = lazy(() => import('./components/PWAUpdatePrompt'));
const ErrorBoundary    = lazy(() => import('./components/ErrorBoundary'));
const AnalyticsTracker = lazy(() => import('./components/AnalyticsTracker'));

import { canAccessFinancialModules } from './utils/roles';
import { isModuleEnabled } from './config/farmConfig';
import PageErrorBoundary from './components/shared/PageErrorBoundary';

// ─── Protected route — redirects to /login if not authenticated ───────────────

function ProtectedRoute() {
  const { isLoggedIn } = useAuth();
  return isLoggedIn ? <Outlet /> : <Navigate to="/login" replace />;
}

// ─── Public-only route — redirects to / if already authenticated ──────────────

function PublicOnlyRoute() {
  const { isLoggedIn } = useAuth();
  return isLoggedIn ? <Navigate to="/" replace /> : <Outlet />;
}

function FinancialRoute() {
  const { user } = useAuth();
  return canAccessFinancialModules(user?.role) ? <Outlet /> : <Navigate to="/" replace />;
}

// Gates a route group to farms that actually have `module` active — checks
// both the deployment-wide build flag (VITE_ENABLED_MODULES) and the
// logged-in tenant's own activeModules, so a Poultry-only farm can't land on
// Dairy pages (or vice versa) via deep link, browser back/forward, or a
// stale bookmark just because the tab/sidebar happens to hide the link.
function ModuleRoute({ module }) {
  const { hasModule } = useFarmModules();
  return isModuleEnabled(module) && hasModule(module) ? <Outlet /> : <Navigate to="/" replace />;
}

// ─── Load all data once when user is (or becomes) logged in ───────────────────

function DataLoader() {
  const { isLoggedIn } = useAuth();
  const { actions, state } = useApp();
  const loadedRef = useRef(false);

  useEffect(() => {
    if (isLoggedIn && !loadedRef.current) {
      loadedRef.current = true;
      actions.loadAllData();
    }
    if (!isLoggedIn) {
      if (loadedRef.current) actions.resetData();
      loadedRef.current = false;
    }
  }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

// ─── Route wrapper with per-page ErrorBoundary + Suspense ─────────────────────
function LazyRoute({ children }) {
  return (
    <PageErrorBoundary>
      <Suspense fallback={<PageSkeleton />}>
        {children}
      </Suspense>
    </PageErrorBoundary>
  );
}

function TenantApp() {
  return (
    <Suspense fallback={<PageSkeleton />}>
      <ErrorBoundary>
        <AuthProvider>
          <PlanProvider>
            <FarmModuleProvider>
            <AppProvider>
              <ToastProvider>
                <DataLoader />
                <LazyRoute>
                  <PWAInstallPrompt />
                  <PWAUpdatePrompt />
                </LazyRoute>
                <BrowserRouter>
                  <LazyRoute>
                    <AnalyticsTracker />
                  </LazyRoute>
                  <Routes>
                    {/* Fully public routes — no auth required */}
                    <Route path="/privacy"          element={<LazyRoute><PrivacyPolicy /></LazyRoute>} />
                    <Route path="/forgot-password"  element={<LazyRoute><ForgotPassword /></LazyRoute>} />
                    <Route path="/reset-password"   element={<LazyRoute><ResetPassword /></LazyRoute>} />
                    <Route path="/verify-email"     element={<LazyRoute><VerifyEmail /></LazyRoute>} />
                    <Route path="/registration-success" element={<LazyRoute><RegistrationSuccess /></LazyRoute>} />
                    <Route path="/forbidden"        element={<LazyRoute><Forbidden /></LazyRoute>} />

                    {/* Public routes (Login / Register) */}
                    <Route element={<PublicOnlyRoute />}>
                      <Route path="/login"    element={<Login />} />
                      <Route path="/register" element={<LazyRoute><Register /></LazyRoute>} />
                    </Route>

                    {/* Protected app routes */}
                    <Route element={<ProtectedRoute />}>
                      <Route element={<Layout />}>
                        <Route path="/"              element={<LazyRoute><Dashboard /></LazyRoute>} />
                        <Route path="/insights"      element={<LazyRoute><Insights /></LazyRoute>} />
                        <Route element={<ModuleRoute module="POULTRY" />}>
                          <Route path="/rooms"         element={<LazyRoute><Rooms /></LazyRoute>} />
                          <Route path="/rooms/view"    element={<LazyRoute><RoomDetail /></LazyRoute>} />
                          <Route path="/egg-entry"     element={<LazyRoute><EggEntry /></LazyRoute>} />
                          <Route path="/incubator"     element={<LazyRoute><Incubator /></LazyRoute>} />
                        </Route>
                        <Route element={<ModuleRoute module="DAIRY" />}>
                          <Route path="/dairy/sheds"         element={<LazyRoute><DairySheds /></LazyRoute>} />
                          <Route path="/dairy/sheds/view"    element={<LazyRoute><DairyShedProfile /></LazyRoute>} />
                          <Route path="/dairy/animals"       element={<LazyRoute><DairyAnimals /></LazyRoute>} />
                          <Route path="/dairy/animals/view"  element={<LazyRoute><DairyAnimalProfile /></LazyRoute>} />
                          <Route path="/dairy/milk"          element={<LazyRoute><DairyMilk /></LazyRoute>} />
                          <Route path="/dairy/health"        element={<LazyRoute><DairyHealth /></LazyRoute>} />
                          <Route path="/dairy/breeding"      element={<LazyRoute><DairyBreeding /></LazyRoute>} />
                          <Route element={<FinancialRoute />}>
                            <Route path="/dairy/trading"      element={<LazyRoute><DairyTrading /></LazyRoute>} />
                            <Route path="/dairy/trading/view" element={<LazyRoute><DairyTradingPartyProfile /></LazyRoute>} />
                          </Route>
                        </Route>
                        <Route path="/notifications" element={<LazyRoute><Notifications /></LazyRoute>} />
                        <Route path="/profile"       element={<LazyRoute><Profile /></LazyRoute>} />
                        <Route element={<FinancialRoute />}>
                          <Route path="/inventory"     element={<LazyRoute><Inventory /></LazyRoute>} />
                          <Route path="/inventory/view" element={<LazyRoute><InventoryDetail /></LazyRoute>} />
                          <Route path="/reports"       element={<LazyRoute><Reports /></LazyRoute>} />
                          <Route path="/upgrade"       element={<LazyRoute><Upgrade /></LazyRoute>} />
                          <Route path="/expenses"      element={<LazyRoute><Expenses /></LazyRoute>} />
                          <Route path="/expenses/comparison" element={<LazyRoute><ExpenseComparison /></LazyRoute>} />
                          <Route path="/expenses/history" element={<LazyRoute><ExpenseHistory /></LazyRoute>} />
                          <Route path="/sales"         element={<LazyRoute><Sales /></LazyRoute>} />
                          <Route path="/sales/history" element={<LazyRoute><SaleHistory /></LazyRoute>} />
                          <Route path="/employees"     element={<LazyRoute><Employees /></LazyRoute>} />
                          <Route path="/employees/:id" element={<LazyRoute><EmployeeDetail /></LazyRoute>} />
                          <Route path="/attendance"    element={<LazyRoute><Attendance /></LazyRoute>} />
                          <Route path="/payroll"       element={<LazyRoute><Payroll /></LazyRoute>} />
                        </Route>
                        <Route path="*"              element={<LazyRoute><NotFound /></LazyRoute>} />
                      </Route>
                    </Route>
                  </Routes>
                </BrowserRouter>
              </ToastProvider>
            </AppProvider>
            </FarmModuleProvider>
          </PlanProvider>
        </AuthProvider>
      </ErrorBoundary>
    </Suspense>
  );
}

// The product-owner console is a separate, self-contained app: no tenant providers, no data
// loading, its own auth (sessionStorage token) — see src/console/.
const ConsoleApp = lazy(() => import('./console/ConsoleApp'));

export default function App() {
  if (window.location.pathname === '/console' || window.location.pathname.startsWith('/console/')) {
    return (
      <Suspense fallback={<PageSkeleton />}>
        <ConsoleApp />
      </Suspense>
    );
  }
  return <TenantApp />;
}
